/**********************************************************************
 *
 * Copyright (C) 2022 Almendo Technologies GmbH
 * Copyright (C) 2022 Marcus Harringer
 *
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of
 * Marcus Harringer. The intellectual and technical concepts contained
 * herein are proprietary to Marcus Harringer and may be covered
 * copyright law. Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written permission
 * is obtained from Marcus Harringer.
 *
 ***********************************************************************/
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Alert, Box, Button, Link, TextField, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import { useTranslation } from 'react-i18next';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import useAuth from '../../../hooks/useAuth';
import useMounted from '../../../hooks/useMounted';
import { translateAuthError } from '../cognitoErrors';
import { hasCompanyAccount, isSsoEnabled } from '../../../utils/ssoLogin';

const MicrosoftIcon = () => <FontAwesomeIcon icon={faMicrosoft} />;

/**********************************************************************
 * STEPS
 * Email first: the address decides which ways to sign in are offered.
 *   EMAIL    address only
 *   METHOD   company account (Microsoft, company domains only), password or code by email
 *   PASSWORD password field for the address
 *   CODE     one-time code Cognito sent to the address
***********************************************************************/
export const LoginStep = {
  EMAIL: 'email',
  METHOD: 'method',
  PASSWORD: 'password',
  CODE: 'code'
};


/**********************************************************************
 * PAGE
***********************************************************************/
const LoginAmplify = (props) => {
  const mounted = useMounted();
  const navigate = useNavigate();
  const { login, loginWithCompanyAccount, sendLoginCode, loginWithCode } = useAuth();
  const { t } = useTranslation('nls');
  const ssoEnabled = isSsoEnabled();
  const [step, setStep] = useState(LoginStep.EMAIL);
  const [company, setCompany] = useState(false);

  // accounts created via Microsoft have neither password nor code, the hint keeps the user from guessing
  const signInError = (err) => (company ? `${translateAuthError(t, err)}\n${t('auth.NLS_LOGIN_COMPANY_ONLY')}` : translateAuthError(t, err));

  return (
    <Formik
      initialValues={{
        email: '',
        password: '',
        code: '',
        submit: null
      }}
      validationSchema={Yup
        .object()
        .shape({
          email: Yup
            .string()
            .email(t('auth.NLS_VALIDATION_EMAIL'))
            .max(255)
            .required(t('auth.NLS_VALIDATION_EMAIL_REQUIRED')),
          password: step === LoginStep.PASSWORD
            ? Yup.string().max(255).required(t('auth.NLS_VALIDATION_PASSWORD_REQUIRED'))
            : Yup.string(),
          code: step === LoginStep.CODE
            ? Yup.string().max(255).required(t('auth.NLS_VALIDATION_CODE_REQUIRED'))
            : Yup.string()
        })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting, setTouched }) => {
        // step EMAIL: choose between the ways to sign in of the address, straight to the password when there is only that
        if (step === LoginStep.EMAIL) {
          const companyAccount = ssoEnabled && await hasCompanyAccount(values.email);
          if (mounted.current) {
            setCompany(companyAccount);
            setStep(LoginStep.METHOD);
            setTouched({}, false);
            setSubmitting(false);
          }
          return;
        }

        try {
          if (step === LoginStep.CODE) {
            await loginWithCode(values.code);
          } else {
            await login(values.email, values.password);
          }

          if (mounted.current) {
            setStatus({ success: true });
            setSubmitting(false);
          }
        } catch (err) {
          console.error(err);

          if (err.name === 'UserNotConfirmedException') {
            navigate('/authentication/verify-code', {
              state: {
                username: values.email
              }
            });
            return;
          }

          if (mounted.current) {
            setStatus({ success: false });
            setErrors({ submit: signInError(err) });
            setSubmitting(false);
          }
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setErrors, setFieldValue, setSubmitting, setTouched, touched, values }) => {
        // redirect to the identity provider. Buttons are enabled again right away, the browser keeps this state
        // when the user comes back with "back".
        const redirect = async (signIn) => {
          setSubmitting(true);
          try {
            await signIn();
          } catch (err) {
            console.error(err);
            setErrors({ submit: translateAuthError(t, err) });
          } finally {
            setSubmitting(false);
          }
        };

        const changeEmail = () => {
          setErrors({});
          setStep(LoginStep.EMAIL);
        };

        // Cognito mails a new code on every request, the previous one is no longer valid
        const sendCode = async () => {
          setSubmitting(true);
          setErrors({});
          try {
            await sendLoginCode(values.email);
            if (mounted.current) {
              setFieldValue('code', '', false);
              setTouched({}, false);
              setStep(LoginStep.CODE);
            }
          } catch (err) {
            console.error(err);
            if (err.name === 'UserNotConfirmedException') {
              navigate('/authentication/verify-code', {
                state: {
                  username: values.email
                }
              });
              return;
            }
            setErrors({ submit: signInError(err) });
          } finally {
            if (mounted.current) {
              setSubmitting(false);
            }
          }
        };

        return (
          <form
            noValidate
            onSubmit={handleSubmit}
            {...props}
          >
            {step === LoginStep.EMAIL ? (
              <TextField
                autoComplete="username"
                autoFocus
                error={Boolean(touched.email && errors.email)}
                fullWidth
                helperText={touched.email && errors.email}
                label={t('auth.NLS_EMAIL')}
                margin="normal"
                name="email"
                onBlur={handleBlur}
                onChange={handleChange}
                type="email"
                value={values.email}
                variant="outlined"
              />
            ) : (
              <Box
                sx={{
                  alignItems: 'center',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'flex',
                  gap: 1.5,
                  mt: 2,
                  px: 2,
                  py: 1.5
                }}
              >
                <AccountCircleOutlinedIcon color="primary" />
                <Typography
                  color="textPrimary"
                  sx={{ flexGrow: 1, fontWeight: 600, wordBreak: 'break-all' }}
                  variant="subtitle1"
                >
                  {values.email}
                </Typography>
                <Button
                  onClick={changeEmail}
                  size="small"
                  type="button"
                >
                  {t('auth.NLS_LOGIN_CHANGE_EMAIL')}
                </Button>
              </Box>
            )}
            {step === LoginStep.PASSWORD && (
              <TextField
                autoComplete="current-password"
                autoFocus
                error={Boolean(touched.password && errors.password)}
                fullWidth
                helperText={touched.password && errors.password}
                label={t('auth.NLS_PASSWORD')}
                margin="normal"
                name="password"
                onBlur={handleBlur}
                onChange={handleChange}
                type="password"
                value={values.password}
                variant="outlined"
              />
            )}
            {step === LoginStep.CODE && (
              <>
                <Typography
                  color="textSecondary"
                  sx={{ mt: 2 }}
                  variant="body2"
                >
                  {t('auth.NLS_LOGIN_EMAIL_CODE_SENT')}
                </Typography>
                <TextField
                  autoComplete="one-time-code"
                  autoFocus
                  error={Boolean(touched.code && errors.code)}
                  fullWidth
                  helperText={touched.code && errors.code}
                  inputProps={{ inputMode: 'numeric' }}
                  label={t('auth.NLS_LOGIN_EMAIL_CODE_LABEL')}
                  margin="normal"
                  name="code"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.code}
                  variant="outlined"
                />
              </>
            )}
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <Alert
                  severity="error"
                  sx={{ whiteSpace: 'pre-line' }}
                >
                  {errors.submit}
                </Alert>
              </Box>
            )}
            {step !== LoginStep.METHOD && (
              <Box sx={{ mt: 2 }}>
                <Button
                  color="primary"
                  disabled={isSubmitting}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                >
                  {step === LoginStep.EMAIL ? t('auth.NLS_LOGIN_NEXT') : t('auth.NLS_LOGIN')}
                </Button>
              </Box>
            )}
            {step === LoginStep.CODE && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                  component="button"
                  disabled={isSubmitting}
                  onClick={sendCode}
                  type="button"
                  underline="hover"
                  variant="body2"
                >
                  {t('auth.NLS_LOGIN_EMAIL_CODE_RESEND')}
                </Link>
              </Box>
            )}
            {step === LoginStep.METHOD && (
              <Box sx={{ mt: 1 }}>
                {company && (
                  <Alert
                    severity="info"
                    sx={{ mt: 2 }}
                  >
                    {t('auth.NLS_LOGIN_COMPANY_ONLY')}
                  </Alert>
                )}
                {company && (
                  <Button
                    color="primary"
                    disabled={isSubmitting}
                    fullWidth
                    onClick={() => redirect(() => loginWithCompanyAccount(values.email))}
                    size="large"
                    startIcon={<MicrosoftIcon />}
                    sx={{ mt: 2 }}
                    variant="contained"
                  >
                    {t('auth.NLS_LOGIN_COMPANY')}
                  </Button>
                )}
                <Button
                  color="primary"
                  disabled={isSubmitting}
                  fullWidth
                  onClick={() => setStep(LoginStep.PASSWORD)}
                  size="large"
                  sx={{ mt: 2 }}
                  variant={company ? 'outlined' : 'contained'}
                >
                  {t('auth.NLS_LOGIN_WITH_PASSWORD')}
                </Button>
                <Button
                  color="primary"
                  disabled={isSubmitting}
                  fullWidth
                  onClick={sendCode}
                  size="large"
                  sx={{ mt: 2 }}
                  variant="outlined"
                >
                  {t('auth.NLS_LOGIN_EMAIL_CODE')}
                </Button>
              </Box>
            )}
            <Box sx={{ mt: 3 }}>
              <Alert severity="info">
                <div>
                  {t('auth.NLS_LOGIN_SUPPORT')}
                  {' '}
                  <b>support@blusensor.com</b>
                </div>
              </Alert>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
};

export default LoginAmplify;
